import { invariant } from '@epic-web/invariant'
import { useLocation } from '@remix-run/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef, useState, useCallback, useEffect } from 'react'
import { useSpinDelay } from 'spin-delay'
import { type ImageOrPlaceholder } from '#app/routes/generate+/_layout'
import { type GeneratedImage2 } from '#app/routes/resources+/generated-images'
import { useKeepMountedRangeExtractor } from '#app/utils/hooks/useKeepMountedRangeExtractor'
import useMasonryColumnCount from '#app/utils/hooks/useMasonryColumnCount'
import useResizeObserver from '#app/utils/hooks/useResizeObserver'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'
import { useSelectionContext } from './image-selection-context'
import { ImageTile } from './masonry-image-tile'
import Spinner from './ui/spinner'

const OVERSCAN = 10
const ESTIMATED_SIZE = 300

interface MasonryProps {
	newImages?: Array<ImageOrPlaceholder>
	isTraining?: boolean
	// captionFields?: Fieldset<Record<string, string>>
	captionFields?: any
	deletedImagesIds?: string[]
	characterSlug?: string
	queryKey?: string
}

async function fetchServerPage(
	page: number = 0,
	take: number = 10,
	isTraining: boolean = false,
	userId?: string,
	characterSlug?: string,
	queryKey?: string,
): Promise<{ images: GeneratedImage2[]; nextOffset: number | null }> {
	const response = await fetch(
		`/resources/gallery?page=${page}&take=${take}&fetchTrainingImages=${isTraining}&userId=${userId}${
			characterSlug ? `&characterSlug=${characterSlug}` : ''
		}&queryKey=${queryKey}`,
	)
	if (!response.ok) {
		throw new Error('Failed to fetch gallery images')
	}
	const data = (await response.json()) as {
		images: GeneratedImage2[]
		nextPage: number
	}

	return {
		images: data.images,
		nextOffset: data.images.length < take ? null : data.nextPage,
	}
}

export function Masonry({
	newImages = [],
	isTraining = false,
	captionFields = {},
	deletedImagesIds = [],
	characterSlug,
	queryKey,
}: MasonryProps) {
	const containerRef = useRef(null)
	const [containerWidth, setContainerWidth] = useState(0)

	useResizeObserver(containerRef, setContainerWidth)
	const [columnCount, columnWidth] = useMasonryColumnCount(containerWidth)

	const { selectedImagesIds } = useSelectionContext()

	const scrollRef = useRef(null)
	const rangeExtractor = useKeepMountedRangeExtractor()

	const location = useLocation()

	const user = useOptionalUser()
	const { data, isFetchingNextPage, fetchNextPage, hasNextPage } =
		useInfiniteQuery({
			queryKey: ['masonry', user?.id, queryKey],
			queryFn: ({ pageParam = 0 }) =>
				fetchServerPage(
					pageParam,
					10,
					isTraining,
					user?.id,
					characterSlug,
					queryKey,
				),
			getNextPageParam: (lastPage) => lastPage.nextOffset,
			initialPageParam: 0,
		})

	let allRows = [
		...newImages,
		...(data ? data.pages.flatMap((d) => d.images) : []),
	]

	const filteredRows = allRows.filter((img) => {
		if ('id' in img) {
			return !deletedImagesIds.includes(img.id)
		}
		return true // Include placeholders
	})

	const [initialRender, setInitialRender] = useState(true)

	useEffect(() => {
		if (filteredRows.length > 0) {
			setInitialRender(false)
		}
	}, [filteredRows.length])

	const rowVirtualizer = useVirtualizer({
		count: filteredRows.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ESTIMATED_SIZE,
		overscan: OVERSCAN,
		lanes: columnCount,
		rangeExtractor,
	})

	const isSelectedImage = useCallback(
		(id: string) => (selectedImagesIds || []).some((img) => img.id === id),
		[selectedImagesIds],
	)

	const showSpinner = useSpinDelay(isFetchingNextPage, {
		delay: 150,
		minDuration: 500,
	})

	useEffect(() => {
		const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse()

		if (!lastItem) {
			return
		}

		if (
			lastItem.index >= allRows.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage().catch((e) => console.error(e))
		}
	}, [
		hasNextPage,
		fetchNextPage,
		allRows.length,
		isFetchingNextPage,
		rowVirtualizer.getVirtualItems(),
	])

	return (
		<div ref={containerRef} className="h-full w-full">
			<div
				className={cn('h-full w-full overflow-auto')}
				ref={scrollRef}
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: 'rgba(155, 155, 155, 0.7) rgba(0, 0, 0, 0.1)',
					scrollbarGutter: 'stable',
				}}
			>
				{!initialRender && (
					<div
						style={{
							height: `${rowVirtualizer.getTotalSize()}px`,
							width: '100%',
							position: 'relative',
						}}
					>
						{columnWidth &&
							rowVirtualizer.getVirtualItems().map((vRow) => {
								if (typeof vRow?.index !== 'undefined') {
									const isLoaderRow = vRow.index > filteredRows.length - 1

									if (isLoaderRow) {
										return (
											hasNextPage &&
											showSpinner && (
												<div
													key={`loader-row-${vRow.index}`}
													style={{
														position: 'absolute',
														bottom: 0,
														left: 0,
														width: '100%',
														height: '50px', // Adjust as needed
														display: 'flex',
														justifyContent: 'center',
														alignItems: 'center',
													}}
												>
													<Spinner />
												</div>
											)
										)
									}

									const image = filteredRows[vRow.index]
									invariant(image, 'image is required')
									const captionEntry =
										'id' in image ? captionFields[image.id] : null

									return (
										<ImageTile
											key={`${location.pathname}-${vRow.index}-${'id' in image ? image.id : 'placeholder'}`}
											vRow={vRow}
											image={image}
											captionField={captionEntry}
											columnWidth={columnWidth}
											containerWidth={containerWidth}
											isSelectedImage={
												'id' in image ? isSelectedImage(image.id) : false
											}
											virtualizerRef={rowVirtualizer.measureElement}
										/>
									)
								}
								return <></>
							})}
					</div>
				)}
			</div>
		</div>
	)
}
